import type { CreateStandaloneIDV } from '@common/types/standaloneIdv';
import { isValidEmail } from '@common/utils/isValidEmail';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Stack,
    TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import LoadingButton from '@/components/Buttons/LoadingButton';
import NewProspectStep from '@/components/NewProspectModal/NewProspectStep';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { invalidateSAIDVList } from '@/queries/saidv';
import { useUsers } from '@/queries/users';
import { createIDV } from '@/services/IDVService';
import useIdentityStore from '@/stores/useIdentityStore';
import { isValidPhone } from '@/utils/phone';
import { successSnackbar, warningSnackbar } from '@/utils/snackbar';
import IDVClientRow from './IDVClientRow';
import IDVCustomSelect from './IDVCustomSelect';
import { type ClientInfo, createEmptyClient } from './types';

interface Props {
    onClose(): void;
}

function hasAllClientValues(client: ClientInfo) {
    return (
        !!client.firstName &&
        !!client.lastName &&
        !!client.contactEmail &&
        isValidEmail(client.contactEmail) &&
        isValidPhone(client.phoneNumber, client.countryCode)
    );
}

function validateCustomFileNumber(value: string) {
    if (value && value.length > 400) {
        return 'Custom file number must be less than 400 characters.';
    }
    return '';
}

function validateForm(
    primaryClientDetails: ClientInfo,
    clientDetails: ClientInfo[],
    assignedUserId: number | null,
) {
    const clientDetailsErrors: Record<string, string> = {};

    if (!assignedUserId || assignedUserId <= 0) {
        clientDetailsErrors.userId = 'Assigned user is required.';
    }

    if (!primaryClientDetails.firstName) {
        clientDetailsErrors.primaryClientFirstName = 'First name is required.';
    }
    if (!primaryClientDetails.lastName) {
        clientDetailsErrors.primaryClientLastName = 'Last name is required.';
    }
    if (!primaryClientDetails.contactEmail) {
        clientDetailsErrors.primaryClientEmail = 'Email is required.';
    } else if (!isValidEmail(primaryClientDetails.contactEmail)) {
        clientDetailsErrors.primaryClientEmail = 'Invalid email format.';
    }
    if (!primaryClientDetails.phoneNumber) {
        clientDetailsErrors.primaryClientCellNumber = 'Cell Number is required.';
    } else if (!isValidPhone(primaryClientDetails.phoneNumber, primaryClientDetails.countryCode)) {
        clientDetailsErrors.primaryClientCellNumber = 'Enter a valid cell number.';
    }

    clientDetails.forEach((client, idx) => {
        if (!client.firstName) {
            clientDetailsErrors[`firstName_${idx}`] = 'First name is required.';
        }
        if (!client.lastName) {
            clientDetailsErrors[`lastName_${idx}`] = 'Last name is required.';
        }
        if (!client.contactEmail) {
            clientDetailsErrors[`contactEmail_${idx}`] = 'Email is required.';
        } else if (!isValidEmail(client.contactEmail)) {
            clientDetailsErrors[`contactEmail_${idx}`] = 'Invalid email format.';
        }
        if (!client.phoneNumber) {
            clientDetailsErrors[`cellNumber_${idx}`] = 'Cell Number is required.';
        } else if (!isValidPhone(client.phoneNumber, client.countryCode)) {
            clientDetailsErrors[`cellNumber_${idx}`] = 'Enter a valid cell number.';
        }
    });

    const allEmails = [
        primaryClientDetails.contactEmail?.toLowerCase().trim(),
        ...clientDetails.map((client) => client.contactEmail?.toLowerCase().trim()),
    ].filter(Boolean);

    const duplicateEmails = allEmails.filter((email, index) => allEmails.indexOf(email) !== index);

    if (duplicateEmails.length) {
        duplicateEmails.forEach((dupEmail) => {
            if (primaryClientDetails.contactEmail?.toLowerCase().trim() === dupEmail) {
                clientDetailsErrors.primaryClientEmail = 'Duplicate email. Must be unique.';
            }

            clientDetails.forEach((client, idx) => {
                if (client.contactEmail?.toLowerCase().trim() === dupEmail) {
                    clientDetailsErrors[`contactEmail_${idx}`] = 'Duplicate email. Must be unique.';
                }
            });
        });
    }

    return clientDetailsErrors;
}

function createPayload(
    primaryClientDetails: ClientInfo,
    clientDetails: ClientInfo[],
    assignedUser: { userId: number; signature: string | null },
    ccPerson: string[],
    sharedFields: {
        initiatorName: string;
        initiatorEmail: string;
        initiatorOrganizationName: string;
        requesterReferenceId: string;
    },
): CreateStandaloneIDV {
    const primaryClient = {
        ...primaryClientDetails,
        ...sharedFields,
    };

    const otherClients = clientDetails.map((client) => ({
        ...client,
        ...sharedFields,
    }));

    return {
        standaloneProfiles: [primaryClient, ...otherClients],
        standaloneProspectOverride: {
            approverId: assignedUser.userId,
            intakeEmailSignature: assignedUser.signature,
            intakeCCEmails: ccPerson,
        },
    };
}

export default function IDVModal({ onClose }: Props) {
    const queryClient = useQueryClient();
    const [isCloseAlert, setIsCloseAlert] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [primaryClientDetails, setPrimaryClientDetails] = useState(() => createEmptyClient(true));
    const [clientDetails, setClientDetails] = useState<ClientInfo[]>([]);
    const [assignedUserId, setAssignedUserId] = useState<number | null>(null);
    const [ccPerson, setCcPerson] = useState<string[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [customFileNumberError, setCustomFileNumberError] = useState('');

    const loggedInUser = useIdentityStore((state) => state.user);
    const { activeLawFirmId } = useLawFirmContext();
    const { data: activeLawFirm } = useLawFirm(activeLawFirmId);
    const { data: users = [] } = useUsers({ lawFirmId: activeLawFirmId });

    // Default to the current user when they belong to the active firm; otherwise
    // require an explicit selection (e.g. Super Admin acting as another firm).
    const effectiveAssignedUserId =
        assignedUserId ??
        (loggedInUser && users.some((user) => user.userId === loggedInUser.userId)
            ? loggedInUser.userId
            : null);

    const assignedUser = users.find((user) => user.userId === effectiveAssignedUserId);

    const assignedLawyerOptions = users.map((user) => ({
        label: user.fullName,
        value: user.userId,
    }));

    const availableCcContacts = users
        .map((user) => user.email)
        .filter((email) => email !== assignedUser?.email);

    const isClientDetailsValid = clientDetails.every(hasAllClientValues);
    const isPrimaryClientDetailsValid = hasAllClientValues(primaryClientDetails);
    const isValid = isClientDetailsValid && isPrimaryClientDetailsValid;

    const updatePrimaryClient = <K extends keyof ClientInfo>(key: K, value: ClientInfo[K]) => {
        setPrimaryClientDetails((prev) => ({ ...prev, [key]: value }));
    };

    const updateClient = <K extends keyof ClientInfo>(
        index: number,
        key: K,
        value: ClientInfo[K],
    ) => {
        setClientDetails((prev) =>
            prev.map((client, idx) => (idx === index ? { ...client, [key]: value } : client)),
        );
    };

    const handleDeleteClient = (index: number) => {
        setClientDetails((prev) => prev.filter((_, i) => i !== index));
        setErrors({});
    };

    const handleCountryChange = (
        country: { code: string; dialCode: string },
        idx: number | null,
    ) => {
        if (idx !== null) {
            updateClient(idx, 'dialCode', +country.dialCode);
            updateClient(idx, 'countryCode', country.code);
        } else {
            updatePrimaryClient('dialCode', +country.dialCode);
            updatePrimaryClient('countryCode', country.code);
        }
    };

    const handleAssignedUserChange = (selectedUserId: number) => {
        setAssignedUserId(selectedUserId);

        const selectedUser = users.find((user) => user.userId === selectedUserId);
        if (selectedUser?.email) {
            setCcPerson((prev) => prev.filter((email) => email !== selectedUser.email));
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm(
            primaryClientDetails,
            clientDetails,
            effectiveAssignedUserId,
        );
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) return;
        if (customFileNumberError) return;
        if (!assignedUser) return;

        const sharedFields = {
            requesterReferenceId: assignedUser.userId.toString(),
            initiatorName: assignedUser.fullName,
            initiatorEmail: assignedUser.email,
            initiatorOrganizationName: activeLawFirm?.companyName || '',
        };

        const payload = createPayload(
            primaryClientDetails,
            clientDetails,
            assignedUser,
            ccPerson,
            sharedFields,
        );

        setIsLoading(true);
        try {
            await createIDV(payload);
            successSnackbar('IDV filled successfully');
            invalidateSAIDVList(queryClient);
            onClose();
        } catch (error: unknown) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog
                open
                maxWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        width: '90vW',
                    },
                }}
            >
                <div
                    style={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        color: 'white',
                        padding: '16px 20px',
                        textAlign: 'center',
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '20px',
                            fontWeight: 500,
                            marginBottom: '4px',
                            paddingBottom: '0px',
                        }}
                    >
                        Identity Verification
                    </DialogTitle>
                    <DialogTitle
                        sx={{
                            opacity: 0.9,
                            fontSize: '13px',
                            padding: '0px',
                        }}
                    >
                        Send an identity verification request to your client
                    </DialogTitle>
                </div>
                <DialogContent sx={THIN_SCROLLBAR_STYLING}>
                    <NewProspectStep
                        stepNumber="1"
                        title="Client Information"
                        description="Add primary client and additional parties"
                        isComplete={isValid}
                        showStatus={false}
                    >
                        <Stack gap={3}>
                            <Stack direction="row" gap={1.5} alignItems="flex-start">
                                <FormControl fullWidth error={!!customFileNumberError}>
                                    <TextField
                                        fullWidth
                                        label="Custom File Number (optional)"
                                        placeholder="Enter custom file number (optional)"
                                        value={primaryClientDetails.customFileNumber}
                                        sx={{ backgroundColor: 'white' }}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            updatePrimaryClient('customFileNumber', value);
                                            setCustomFileNumberError(
                                                validateCustomFileNumber(value),
                                            );
                                        }}
                                    />
                                    <FormHelperText
                                        sx={
                                            customFileNumberError
                                                ? { color: 'error.main' }
                                                : undefined
                                        }
                                    >
                                        {customFileNumberError ||
                                            'Enter your own file number to be displayed in this portal and in emails about the transaction.'}
                                    </FormHelperText>
                                </FormControl>
                                <IDVCustomSelect
                                    label="Assigned User"
                                    value={effectiveAssignedUserId}
                                    options={assignedLawyerOptions}
                                    onChange={handleAssignedUserChange}
                                    error={errors.userId}
                                />
                                <FormControl fullWidth>
                                    <AutoCompleteMultiSelect
                                        fullWidth
                                        options={availableCcContacts}
                                        getOptionLabel={(option) => option}
                                        label="CC'd Person"
                                        onChange={(_e, value) => {
                                            setCcPerson(value);
                                        }}
                                        disabled={false}
                                        title=""
                                        value={ccPerson}
                                        sx={{ backgroundColor: 'white' }}
                                    />
                                </FormControl>
                            </Stack>

                            <IDVClientRow
                                title="Primary Client Details"
                                client={primaryClientDetails}
                                required
                                errors={{
                                    firstName: errors.primaryClientFirstName,
                                    lastName: errors.primaryClientLastName,
                                    contactEmail: errors.primaryClientEmail,
                                    phoneNumber: errors.primaryClientCellNumber,
                                }}
                                onChange={updatePrimaryClient}
                                onCountryChange={(country) => handleCountryChange(country, null)}
                                onResetErrors={() => setErrors({})}
                            />

                            {clientDetails.length > 0 && (
                                <Stack gap={3}>
                                    {clientDetails.map((client, idx) => (
                                        <IDVClientRow
                                            key={idx}
                                            title={`Additional Client ${idx + 1}`}
                                            client={client}
                                            errors={{
                                                firstName: errors[`firstName_${idx}`],
                                                lastName: errors[`lastName_${idx}`],
                                                contactEmail: errors[`contactEmail_${idx}`],
                                                phoneNumber: errors[`cellNumber_${idx}`],
                                            }}
                                            onChange={(field, value) =>
                                                updateClient(idx, field, value)
                                            }
                                            onCountryChange={(country) =>
                                                handleCountryChange(country, idx)
                                            }
                                            onRemove={() => handleDeleteClient(idx)}
                                            onResetErrors={() => setErrors({})}
                                        />
                                    ))}
                                </Stack>
                            )}

                            <Button
                                type="button"
                                variant="contained"
                                startIcon={<AddOutlinedIcon />}
                                onClick={() => {
                                    if (clientDetails.length < 9) {
                                        setClientDetails((prev) => [
                                            ...prev,
                                            createEmptyClient(false),
                                        ]);
                                    } else {
                                        warningSnackbar('You can add up to 9 additional clients.');
                                    }
                                }}
                                sx={{ width: 'fit-content' }}
                            >
                                Add Another Client
                            </Button>
                        </Stack>
                    </NewProspectStep>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setIsCloseAlert(true)}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        onClick={handleSubmit}
                        autoFocus
                    >
                        Finish
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AlertDialog
                open={isCloseAlert}
                handleClose={() => setIsCloseAlert(false)}
                handleConfirm={onClose}
                title="Are you sure?"
                message="Closing the popup will result in loss of any data that you have filled up. Are you sure you want to close the popup?"
            />
        </>
    );
}
